import type { ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { cva, type VariantProps } from "@/shared/lib/variants";

/**
 * EmptyState — the only sanctioned shape for "no data yet" / terminal-status
 * surfaces (design.md §10). Rolling a bespoke empty layout is out of spec.
 *
 * - inline (default): title in type-heading-sm, internal py-16. Wrap with the
 *   page's normal <Section><Container> shell.
 * - page: title is an <h1> in type-heading-lg. Pair with a full-viewport
 *   centering shell.
 */
const emptyState = cva("text-center flex flex-col items-center", {
  variants: {
    variant: {
      inline: "py-16 gap-4",
      page: "gap-6",
    },
  },
  defaultVariants: {
    variant: "inline",
  },
});

export interface EmptyStateProps extends VariantProps<typeof emptyState> {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  description,
  action,
  variant = "inline",
  className,
}: EmptyStateProps) {
  return (
    <div className={cn(emptyState({ variant }), className)}>
      {variant === "page" ? (
        <h1 className="type-heading-lg text-text">{title}</h1>
      ) : (
        <p className="type-heading-sm text-text">{title}</p>
      )}
      {description ? (
        <p className="type-body-base text-text-muted max-w-prose">{description}</p>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
